import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  RefreshCw,
  Trash2,
  Eye,
  EyeOff,
  Pencil,
  ArrowUp,
  ArrowDown,
  Quote,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { useAdminList, API, authHeaders } from "./adminApi";

export default function TestimonialsAdmin({ token, onUnauthorized }) {
  const { data, loading, refresh, setData } = useAdminList(
    token,
    "/admin/testimonials",
    onUnauthorized,
  );
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: "", text: "", visible: true });

  const close = () => {
    setEditing(null);
    setCreating(false);
  };

  const save = async () => {
    try {
      if (creating) {
        const res = await axios.post(`${API}/admin/testimonials`, draft, {
          headers: authHeaders(token),
        });
        setData((d) => [...d, res.data]);
        toast.success("Avis ajouté");
      } else if (editing) {
        const res = await axios.put(
          `${API}/admin/testimonials/${editing.id}`,
          draft,
          { headers: authHeaders(token) },
        );
        setData((d) => d.map((x) => (x.id === editing.id ? res.data : x)));
        toast.success("Avis modifié");
      }
      close();
    } catch {
      toast.error("Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    await axios.delete(`${API}/admin/testimonials/${id}`, {
      headers: authHeaders(token),
    });
    setData((d) => d.filter((x) => x.id !== id));
    toast.success("Avis supprimé");
  };

  const toggle = async (item) => {
    const res = await axios.put(
      `${API}/admin/testimonials/${item.id}`,
      { name: item.name, text: item.text, visible: !item.visible },
      { headers: authHeaders(token) },
    );
    setData((d) => d.map((x) => (x.id === item.id ? res.data : x)));
  };

  const move = async (idx, delta) => {
    const next = [...data];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setData(next);
    await axios.patch(
      `${API}/admin/testimonials/reorder`,
      { ids: next.map((x) => x.id) },
      { headers: authHeaders(token) },
    );
  };

  return (
    <div data-testid="admin-testimonials-page" className="space-y-6">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-semibold text-[#1A2E1C]">
            Avis clients
          </h2>
          <p className="text-[#5C705E] mt-1">Affichés dans la section "Avis" de la home.</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={refresh}
            variant="outline"
            className="rounded-full border-[#E5EBE5] text-[#1B5E20] hover:bg-[#E8F5E9]"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
          </Button>
          <Button
            onClick={() => {
              setDraft({ name: "", text: "", visible: true });
              setCreating(true);
            }}
            data-testid="testimonial-create"
            className="rounded-full bg-[#1B5E20] hover:bg-[#164d1a] text-white"
          >
            <Plus className="w-4 h-4 mr-2" /> Ajouter un avis
          </Button>
        </div>
      </header>

      {loading ? (
        <div className="text-center py-20 text-[#5C705E]">Chargement…</div>
      ) : (
        <div className="grid gap-3" data-testid="testimonials-list">
          {data.map((item, idx) => (
            <div
              key={item.id}
              data-testid={`testimonial-${item.id}`}
              className={`bg-white border rounded-2xl p-4 flex flex-col md:flex-row gap-4 hover:shadow-md transition ${
                item.visible ? "border-[#E5EBE5]" : "border-[#FFCC80] bg-[#FFF8E1]"
              }`}
            >
              <div className="flex gap-3 flex-1 min-w-0">
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    className="p-1 rounded hover:bg-[#E8F5E9] disabled:opacity-30"
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => move(idx, 1)}
                    disabled={idx === data.length - 1}
                    className="p-1 rounded hover:bg-[#E8F5E9] disabled:opacity-30"
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                <Quote className="w-6 h-6 text-[#1B5E20]/40 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <strong className="text-[#1A2E1C] font-['Outfit']">{item.name}</strong>
                    {!item.visible && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#FFCC80] text-[#7B5300] font-semibold uppercase tracking-wider">
                        Masqué
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[#5C705E] mt-1 italic">"{item.text}"</p>
                </div>
              </div>
              <div className="flex items-center gap-2 self-end md:self-center">
                <button onClick={() => toggle(item)} className="p-2 rounded-full hover:bg-[#E8F5E9]">
                  {item.visible ? (
                    <Eye className="w-4 h-4 text-[#1B5E20]" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-[#7B5300]" />
                  )}
                </button>
                <button
                  onClick={() => {
                    setDraft({ name: item.name, text: item.text, visible: item.visible });
                    setEditing(item);
                  }}
                  data-testid={`testimonial-edit-${item.id}`}
                  className="p-2 rounded-full hover:bg-[#E8F5E9]"
                >
                  <Pencil className="w-4 h-4 text-[#1B5E20]" />
                </button>
                <button
                  onClick={() => remove(item.id)}
                  className="p-2 rounded-full hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 text-[#D32F2F]/80" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={creating || !!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent
          aria-describedby="testimonial-form-desc"
          className="max-w-xl rounded-3xl p-6 border-[#E5EBE5]"
        >
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-2xl font-semibold text-[#1A2E1C]">
              {creating ? "Ajouter un avis" : "Modifier l'avis"}
            </DialogTitle>
            <p id="testimonial-form-desc" className="text-sm text-[#5C705E]">
              Affiché sur la page d'accueil.
            </p>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="mb-1.5 block">Auteur</Label>
              <Input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Claire"
                className="bg-white border-[#E5EBE5] rounded-xl h-11"
              />
            </div>
            <div>
              <Label className="mb-1.5 block">Témoignage</Label>
              <Textarea
                value={draft.text}
                onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                rows={5}
                placeholder="Travail soigné, je recommande !"
                className="bg-white border-[#E5EBE5] rounded-xl"
              />
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={draft.visible}
                onChange={(e) => setDraft({ ...draft, visible: e.target.checked })}
                className="w-5 h-5"
              />
              <span className="text-sm">Visible sur le site</span>
            </label>
          </div>
          <div className="mt-6 flex gap-3 justify-end">
            <Button variant="outline" onClick={close} className="rounded-full">
              Annuler
            </Button>
            <Button
              onClick={save}
              disabled={!draft.name || !draft.text}
              className="rounded-full bg-[#1B5E20] hover:bg-[#164d1a] text-white"
            >
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
